import { RESOURCE_NAMES, RESOURCE_TYPES, type ResourceType } from '@kaeliux/shared';
import { formatFullNumber } from './formatters';

export type ResourceKey = ResourceType | 'scrap';

export type ResourceQuantityMap = Partial<Record<ResourceKey, number>>;

export const RESOURCE_QUANTITY_ORDER: readonly ResourceKey[] = [...RESOURCE_TYPES, 'scrap'];

export const RESOURCE_LABELS: Record<ResourceKey, string> = RESOURCE_NAMES;

/**
 * The resources actually worth showing, in canonical order. Amounts are
 * floored first, so a fractional sliver that would read as "0" is dropped
 * rather than rendered. `includeZero` keeps every finite value that was
 * explicitly given — zero (a spy report's "0 gas" is information) and negatives
 * (net energy can be below zero) — while still skipping absent, NaN and
 * infinite ones.
 */
export function resourceEntries(
    quantities: ResourceQuantityMap,
    { includeZero = false }: { includeZero?: boolean } = {},
): [ResourceKey, number][] {
    return RESOURCE_QUANTITY_ORDER.flatMap((key) => {
        const value = Math.floor(quantities[key] ?? NaN);
        const shown = Number.isFinite(value) && (includeZero || value > 0);
        return shown ? [[key, value] as [ResourceKey, number]] : [];
    });
}

export function hasResourceQuantities(quantities: ResourceQuantityMap): boolean {
    return resourceEntries(quantities).length > 0;
}

/**
 * Plain-text form for places that cannot render an icon (confirm dialogs,
 * `title` tooltips) — names the resource instead of using a glyph.
 */
export function formatResourceText(quantities: ResourceQuantityMap, empty = 'free'): string {
    const parts = resourceEntries(quantities).map(
        ([key, value]) => `${formatFullNumber(value)} ${RESOURCE_LABELS[key]}`,
    );
    return parts.length > 0 ? parts.join(', ') : empty;
}
